/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';
import Icon from '../Icon';
import Colors from 'constants/Colors';
import Label from './Label';
import ValidationError from './ValidationError';
import IMask from 'imask';
import { IMaskMixin } from 'react-imask';

import Spinner, { SpinnerSize } from '../Spinner';
import { FiraSansBold, FontWeight } from '../Text';
import Button from '../ButtonForCalendar';
import { Breakpoints } from "../../constants/Breakpoints";
import { mediaHelper } from "../../utils/src";
import { ChangePasswordContainer } from 'containers/change_password/styles';

export const InputSize = {
    SMALL: 1,
    NORMAL: 2
};

const propTypes = {
    type: PropTypes.string,
    placeholder: PropTypes.string,
    mask: PropTypes.any,
    leftIcon: PropTypes.string,
    rightIcon: PropTypes.string,
    leftRender: PropTypes.func,
    rightRender: PropTypes.func,
    isLoading: PropTypes.bool,
    label: PropTypes.string,
    format: PropTypes.func,
    parse: PropTypes.func,
    field: PropTypes.any.isRequired,
    form: PropTypes.any.isRequired,
    isRequired: PropTypes.bool,
    tabIndex: PropTypes.number,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onPressEnter: PropTypes.func,
    fieldProps: PropTypes.any,
    smallInput: PropTypes.bool,
    readOnly: PropTypes.bool,
    inline: PropTypes.bool,
    inverseBlue: PropTypes.bool,
    size: PropTypes.oneOf(Object.values(InputSize)),
    validation: PropTypes.bool,
    valueCalendar: PropTypes.any,
    originalSize: PropTypes.bool,
    customValue: PropTypes.string,
};

const defaultProps = {
    type: 'text',
    mask: null,
    placeholder: '',
    leftIcon: null,
    rightIcon: null,
    leftRender: null,
    rightRender: null,
    label: null,
    format: value => value,
    parse: value => value,
    isLoading: false,
    isRequired: false,
    tabIndex: 0,
    onChange: value => value,
    onBlur: value => value,
    onFocus: value => value,
    onPressEnter: value => value,
    fieldProps: {},
    smallInput: false,
    readOnly: false,
    inline: false,
    inverseBlue: false,
    size: InputSize.NORMAL,
    validation: true,
    valueCalendar: null,
    originalSize: false,
    customValue: null,
};

// Mixins
const errorInputMixin = css`
  box-shadow: 0 0 0 3px ${Colors.DANGER};

  &:focus {
    box-shadow: 0 0 0 3px ${Colors.DANGER};
  }
`;

// Components
export const InputContainer = styled.div`
  box-sizing: border-box;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  margin-bottom: 10px;
  height: 42px;

  box-shadow: 0 0 0 1px ${Colors.GRAY[3]};

  transition: box-shadow 0.15s ease;

  ${({ isFocused }) =>
        isFocused &&
        css`
      box-shadow: 0 0 0 3px ${Colors.SECONDARY};
      ${Input} {
        background: ${Colors.GRAY[5]}
        box-shadow: inset 0px 2px 6px rgba(0, 0, 0, 0.2);
      }
    `}

  ${({ inline }) =>
        inline &&
        css`
      width: 60%;
      min-width: 60%
    `}

  ${({ meta }) => (meta && meta.error ? errorInputMixin : '')}

  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }

  p {
    width: 34px;
    height: 25px;
    font-size: 14px;
    padding-top: 3px;
    padding-left: 2px;
    color: #666666;
    font-family: 'FiraSans',sans-serif;
    font-weight: bold;
    font-style: normal;
  }
`;

export const Input = styled.input`
  scroll-margin: 40px;
  width: ${({ smallInput }) => (smallInput ? '40px' : '100%')};
  min-width: 0;
  padding: 4px;
  border: 0;
  background: white;
  appearance: none;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansBold}

  transition: background 0.15s ease, box-shadow 0.15s ease;

  height: 42px;
  min-height: unset;

  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
  }

  &::placeholder {
    color: ${Colors.GRAY[3]};
  }

  &[disabled] {
    background-color: ${Colors.GRAY[4]};
    color: ${Colors.GRAY[0]};
  }
`;

export const IconButton = styled(Button).attrs({
    type: 'button',
    tabIndex: -1
})`
  padding: 0;
  height: 24px;

  ${mediaHelper.sm`
    height: unset;
  `}

  & ${Icon} {
    font-size: 14px;
    color: ${Colors.GRAY[3]};
    width: 30px;
    height: 30px;
    background-size: 100%;
    margin: 3px;

    ${mediaHelper.sm`
      width: 30px;
      height: 30px;
      margin: 0 12px;
    `}
  }
`;

const InputSpinner = styled(Spinner)`
  margin: 0 12px;
`;

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 15px;

  ${({ inline }) =>
        inline &&
        css`
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
    `}

    ${({ disabled }) =>
        disabled &&
        css`
        opacity: 0.3;
    `}
`;

const MaskedInput = IMaskMixin(({ inputRef, ...props }) => (
    <Input {...props} ref={inputRef} />
));

const InputField = ({
    type,
    placeholder,
    disabled,
    readOnly,
    mask,
    leftIcon,
    leftIconClick,
    leftIconText,
    rightIcon,
    rightIconClick,
    leftRender,
    rightRender,
    isLoading,
    label,
    field,
    format,
    parse,
    size,
    setLabelMinHeight,
    fieldProps,
    isRequired,
    tabIndex,
    form: { setFieldValue, setFieldTouched, touched, errors, setFieldError },
    validationMinHeight,
    hideLabelAsterisk,
    smallInput,
    alignCenter,
    inline,
    validation,
    inverseBlue,
    onInputRef,
    onChange,
    onBlur,
    onFocus,
    onPressEnter,
    valueCalendar,
    originalSize,
    customValue,
    onSearchSelect,
    extraLabelTop,
    key,
    ...props
}) => {
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const firstChange = useRef(false);

    var touchedList;
    var touchedItem;
    var errorList;
    var errorItem;

    const metaFields = field.name.split('.');
    if (metaFields.length == 3) {
        touchedList = touched[metaFields[0]]
        if (touchedList) {
            touchedItem = touchedList[parseInt(metaFields[1])]
        }
        errorList = errors[metaFields[0]]
        if (errorList) {
            errorItem = errorList[parseInt(metaFields[1])]
        }
    }

    const meta = {
        touched: metaFields.length == 3 && touchedItem ? touchedItem[metaFields[2]] || touched[field.name] : touched[field.name],
        error: errors[field.name] || '',
        isFocused: isFocused
    };

    const _onFocus = () => {
        setIsFocused(true);
    };

    const _onBlur = () => {
        setIsFocused(false);
    };

    useEffect(() => {
        setTimeout(() => !firstChange.current ? firstChange.current = true : false, 500)
    }, [])

    if (!mask) mask = { mask: /.*/ }
    return (
        <Wrapper
            inline={inline}
            //disabled={disabled}
            inverse={inverseBlue}
            {...props}
        >
            {(extraLabelTop) && (
                <Label
                    meta={meta}
                    weight={FontWeight.SemiBold}
                    setMinHeight={setLabelMinHeight}
                    hideLabelAsterisk={true}
                    extraLabelTop={inline ? true : false}
                    style={{ marginTop: 20, marginBottom: 20 }}
                    bold
                >
                    {extraLabelTop}
                </Label>
            )}

            {(label || setLabelMinHeight) && (
                <Label
                    meta={meta}
                    width={inline ? 35 : null}
                    isRequired={isRequired}
                    weight={FontWeight.SemiBold}
                    setMinHeight={setLabelMinHeight}
                    inline={inline}
                    inverse={inverseBlue}
                    hideLabelAsterisk={hideLabelAsterisk}
                >
                    {label}
                </Label>
            )}
            <InputContainer
                meta={meta}
                isFocused={meta.isFocused}
                inline={inline}
            >
                {leftIconText && (
                    <p style={{ margin: 0 }}>{leftIconText}</p>
                )}
                <MaskedInput
                    ref={ref => {
                        inputRef.current = ref;
                        if (onInputRef) onInputRef(ref);
                    }}
                    autoComplete="off"
                    name={field.name}
                    key={`${key}-input`}
                    lazy={true}
                    mask={mask?.mask === 'Number' || mask?.mask === 0 ? Number : mask?.mask}
                    blocks={mask?.blocks}
                    scale={mask?.scale}
                    signed={mask?.signed}
                    padFractionalZeros={mask?.padFractionalZeros}
                    radix={mask?.radix}
                    mapToRadix={mask?.mapToRadix || []}
                    thousandsSeparator={mask?.thousandsSeparator}
                    min={mask?.min}
                    max={mask?.max}
                    size={size}
                    type={type}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    tabIndex={tabIndex}
                    onKeyDown={e => {
                        if (e.key.toLowerCase() === 'enter') {
                            onPressEnter(field.value);
                        }
                    }}
                    onFocus={() => {
                        onFocus(field.value);
                        _onFocus();
                    }}
                    onBlur={() => {
                        onBlur(field.value);
                        _onBlur();
                        if (!meta.touched)
                            setFieldTouched(field.name, true)
                    }}
                    smallInput={smallInput}
                    inverseBlue={inverseBlue}
                    alignCenter={alignCenter}
                    value={String(field.value || '')}
                    unmask={true}
                    onChange={(event) => {
                        if (mask) return;
                        onChange(event.target.value);
                        setFieldValue(field.name, event.target.value);
                        console.log("change-valuex", "field.name", event.target.value)
                    }}
                    onAccept={(value, mask) => {
                        if (meta.error) setFieldError(field.name, '')

                        if (!firstChange.current) return firstChange.current = true;
                        if (!mask) return;
                        onChange(value)
                        setFieldValue(field.name, value);
                        console.log("accept-valuex", value, mask)
                    }}
                />
                {rightIcon && (
                    <IconButton onClick={rightIconClick}>
                        <Icon name={rightIcon} />
                    </IconButton>
                )}
                {isLoading && <InputSpinner size={SpinnerSize.SMALL} />}
            </InputContainer>
            {validation && (
                <ValidationError
                    style={{ minWidth: '100%', position: 'absolute', bottom: -38, paddingRight: inline ? 38 : 0 }}
                    meta={meta}
                    validationMinHeight={validationMinHeight}
                />
            )}
        </Wrapper>
    );
};

InputField.propTypes = propTypes;
InputField.defaultProps = defaultProps;

export default InputField;